/**
 * Energy Dashboard Engine Composable
 *
 * Provides reactive state and methods for renewable-energy monitoring.
 *
 * Features:
 * ─ Session / auth integration via window.vcCommunicator
 * ─ Multi-installation weather data fetching (solar + wind)
 * ─ Risk assessment scoring (RAG: 0-100)
 * ─ Solar generation predictions (GHI/DNI/DHI → kW)
 * ─ Wind generation predictions (multi-altitude log-profile → kW)
 * ─ Unit conversion (metric / US)
 */

import { ref, computed, reactive, watch } from 'vue';
import { VcDateUtils } from '/src/visualcrossing-core/vcdateutils';
import { getServerRoot } from '../weatherexceptions/apiConfig.js';

// ─── Constants ────────────────────────────────────────────────────
const SURFACE_ROUGHNESS = 0.1;   // grassland
const REFERENCE_HEIGHT  = 10;    // standard weather station
const CUT_IN  = 3;   // m/s
const RATED   = 15;  // m/s
const CUT_OUT = 25;  // m/s
const SYSTEM_EFFICIENCY = 0.85;  // inverter + wiring losses
const WIND_ALTITUDES = [40, 80, 120, 160]; // metres

// Weather elements needed for solar installations
const SOLAR_ELEMENTS = [
  'datetime', 'datetimeEpoch', 'temp', 'tempmax', 'tempmin',
  'cloudcover', 'humidity', 'windspeed', 'windgust', 'visibility',
  'solarradiation', 'solarenergy', 'uvindex',
  'dniradiation', 'difradiation', 'ghiradiation', 'gtiradiation',
  'sunelevation', 'sunrise', 'sunset', 'conditions', 'icon',
  'precip', 'precipprob', 'snow', 'severerisk', 'description'
].join(',');

// Weather elements needed for wind installations
const WIND_ELEMENTS = [
  'datetime', 'datetimeEpoch', 'temp', 'tempmax', 'tempmin',
  'cloudcover', 'humidity', 'windspeed', 'windgust', 'winddir',
  'windspeed80', 'winddir80', 'windspeed100', 'winddir100',
  'solarradiation', 'solarenergy',
  'dniradiation', 'difradiation', 'ghiradiation', 'gtiradiation',
  'sunelevation', 'sunrise', 'sunset',
  'precip', 'precipprob', 'snow', 'severerisk', 'visibility',
  'conditions', 'icon', 'description'
].join(',');

// All-purpose element list (covers both)
const ALL_ELEMENTS = [
  'datetime', 'datetimeEpoch', 'temp', 'tempmax', 'tempmin',
  'cloudcover', 'humidity', 'windspeed', 'windgust', 'winddir',
  'windspeed80', 'winddir80', 'windspeed100', 'winddir100',
  'solarradiation', 'solarenergy', 'uvindex',
  'dniradiation', 'difradiation', 'ghiradiation', 'gtiradiation',
  'sunelevation', 'sunrise', 'sunset',
  'precip', 'precipprob', 'preciptype', 'snow', 'snowdepth',
  'severerisk', 'visibility', 'pressure',
  'conditions', 'icon', 'description'
].join(',');

// ─── Risk Factor Definitions ─────────────────────────────────────

/**
 * Evaluate a single risk factor, returns 0-100
 */
function _solarCloudRisk(hour) {
  const cc = hour.cloudcover ?? 0;
  const vis = hour.visibility ?? 999;
  if (vis < 1)  return 80;
  if (vis < 5)  return 60;
  if (cc > 80)  return 70;
  if (cc > 60)  return 50;
  if (cc > 40)  return 30;
  return 5;
}

function _solarDustRisk(hour) {
  const ws = hour.windspeed ?? 0;
  const hum = hour.humidity ?? 50;
  if (ws > 10 && hum < 30) return 60;
  if (ws > 7  && hum < 40) return 40;
  return 5;
}

function _solarHeatRisk(hour) {
  const t = hour.temp ?? 25;
  if (t > 45) return 90;
  if (t > 40) return 75;
  if (t > 35) return 60;
  return 5;
}

function _solarHailRisk(hour) {
  const sr = hour.severerisk ?? 0;
  if (sr > 60) return 85;
  if (sr > 30) return 45;
  return 5;
}

function _solarSnowRisk(hour) {
  const t = hour.temp ?? 20;
  const hum = hour.humidity ?? 50;
  const snow = hour.snow ?? 0;
  if (snow > 0) return 80;
  if (t < 0 && hum > 80) return 75;
  if (t < 5 && hum > 90) return 40;
  return 0;
}

function _windStormRisk(hour) {
  const ws = hour.windspeed ?? 0;
  if (ws > 25) return 95;
  if (ws > 20) return 80;
  if (ws < 3)  return 30;
  return 10;
}

function _windIcingRisk(hour) {
  const t = hour.temp ?? 20;
  const hum = hour.humidity ?? 50;
  if (t >= -1 && t <= 3 && hum > 90) return 75;
  if (t < 0 && hum > 80) return 60;
  return 5;
}

function _windTurbulenceRisk(hour, prevHour) {
  if (!prevHour) return 10;
  const avg = ((hour.windspeed || 0) + (prevHour.windspeed || 0)) / 2;
  if (avg === 0) return 10;
  const stddev = Math.abs((hour.windspeed || 0) - (prevHour.windspeed || 0)) / 2;
  const variability = stddev / avg;
  if (variability > 0.5) return 80;
  if (variability > 0.3) return 60;
  if (variability > 0.2) return 40;
  return 10;
}

function _lightningRisk(hour) {
  const sr = hour.severerisk ?? 0;
  const t  = hour.temp ?? 20;
  const hum = hour.humidity ?? 50;
  if (sr > 60) return 90;
  if (sr > 30) return 70;
  if (t > 30 && hum > 70) return 30;
  return 5;
}

function _floodingRisk(hour) {
  const precip = hour.precip ?? 0;
  if (precip > 20) return 85;
  if (precip > 10) return 60;
  if (precip > 5)  return 40;
  return 5;
}

function _temperatureExtremeRisk(hour) {
  const t = hour.temp ?? 20;
  if (t > 45 || t < -20) return 85;
  if (t > 40 || t < -15) return 70;
  return 5;
}

/**
 * Aggregate risk factors with primary/secondary/tertiary weighting
 */
function _aggregateRisks(scores) {
  if (!scores.length) return 0;
  const sorted = [...scores].sort((a, b) => b - a);
  let total = 0;
  const weights = [0.50, 0.30, 0.20];
  for (let i = 0; i < Math.min(sorted.length, 3); i++) {
    total += sorted[i] * (weights[i] || 0.10);
  }
  return Math.min(100, Math.round(total));
}

/**
 * Get risk level label from score
 */
function riskLevel(score) {
  if (score >= 80) return 'extreme';
  if (score >= 60) return 'high';
  if (score >= 30) return 'moderate';
  return 'low';
}

function riskColor(level) {
  switch (level) {
    case 'extreme':  return '#ef4444';
    case 'high':     return '#f97316';
    case 'moderate': return '#eab308';
    default:         return '#22c55e';
  }
}

function riskIcon(level) {
  switch (level) {
    case 'extreme':  return 'bi-exclamation-diamond';
    case 'high':     return 'bi-exclamation-triangle';
    case 'moderate': return 'bi-info-circle';
    default:         return 'bi-check-circle-fill';
  }
}

// ─── Wind profile: logarithmic law ──────────────────────────────
function windSpeedAtHeight(refSpeed, targetHeight) {
  return refSpeed * (Math.log(targetHeight / SURFACE_ROUGHNESS) / Math.log(REFERENCE_HEIGHT / SURFACE_ROUGHNESS));
}

// ─── Wind power from turbine power curve ────────────────────────
function windPower(windSpeed, ratedPowerKW) {
  if (windSpeed < CUT_IN || windSpeed > CUT_OUT) return 0;
  if (windSpeed >= RATED) return ratedPowerKW;
  return ratedPowerKW * (Math.pow(windSpeed, 3) - Math.pow(CUT_IN, 3)) / (Math.pow(RATED, 3) - Math.pow(CUT_IN, 3));
}

// ─── Solar power from GHI ───────────────────────────────────────
function solarPower(ghi, areaSqM, panelEfficiency) {
  if (!ghi || ghi <= 0) return 0;
  return (areaSqM * (ghi / 1000) * panelEfficiency * SYSTEM_EFFICIENCY);  // kW
}

// ════════════════════════════════════════════════════════════════
// ═══ MAIN COMPOSABLE ═══════════════════════════════════════════
// ════════════════════════════════════════════════════════════════

export function useEnergyDashboardEngine(options = {}) {
  const {
    serverRoot: serverRootOverride = null
  } = options;

  // ── Session / Auth ──────────────────────────────────────────
  const sessionId    = ref(null);
  const sessionState = ref(null);
  const isContainerInited = ref(false);
  const serverRoot   = ref(serverRootOverride);

  const hasSession = computed(() => !!sessionId.value && !!sessionState.value);
  const apiKey     = computed(() => sessionState.value?.apiKey || null);

  // ── Configuration ──────────────────────────────────────────
  const unitGroup  = ref('metric');
  const forecastPeriod = ref('next3days');  // next3days, next7days, next14days

  // ── Installations ──────────────────────────────────────────
  const installations = ref([]);
  // Each installation: { id, name, type:'solar'|'wind', lat, lon,
  //   // solar-specific:
  //   areaSqM, peakPowerW, tiltAngle, azimuth,
  //   // wind-specific:
  //   hubHeightM, ratedPowerKW,
  //   // computed at runtime:
  //   weatherData, riskScore, riskLevel, riskFactors, generation, status }

  // ── Run State ──────────────────────────────────────────────
  const isLoading     = ref(false);
  const loadingProgress = ref(0);
  const hasRun        = ref(false);
  const errorMessage  = ref(null);

  // ── Computed Properties ────────────────────────────────────

  const solarInstallations = computed(() => installations.value.filter(i => i.type === 'solar'));
  const windInstallations  = computed(() => installations.value.filter(i => i.type === 'wind'));

  const attentionInstallations = computed(() => {
    return installations.value
      .filter(i => i.riskScore != null && i.riskScore >= 30)
      .sort((a, b) => b.riskScore - a.riskScore);
  });

  const overallSummary = computed(() => {
    const insts = installations.value.filter(i => i.riskScore != null);
    if (!insts.length) return null;
    const maxScore = Math.max(...insts.map(i => i.riskScore));
    const avgScore = Math.round(insts.reduce((s, i) => s + i.riskScore, 0) / insts.length);
    const totalGenKW = insts.reduce((s, i) => s + (i.currentGenKW || 0), 0);
    const totalDailyKWh = insts.reduce((s, i) => s + (i.dailyGenKWh || 0), 0);
    return {
      maxRisk: maxScore,
      avgRisk: avgScore,
      maxRiskLevel: riskLevel(maxScore),
      totalGenKW,
      totalDailyKWh,
      solarCount: solarInstallations.value.length,
      windCount: windInstallations.value.length,
      lowCount:  insts.filter(i => riskLevel(i.riskScore) === 'low').length,
      modCount:  insts.filter(i => riskLevel(i.riskScore) === 'moderate').length,
      highCount: insts.filter(i => riskLevel(i.riskScore) === 'high').length,
      extCount:  insts.filter(i => riskLevel(i.riskScore) === 'extreme').length,
    };
  });

  // ── Installation Management ────────────────────────────────

  let _nextId = 1;

  function addInstallation(inst) {
    const id = inst.id || `inst_${_nextId++}`;
    const entry = {
      id,
      name: inst.name || `Installation ${id}`,
      type: inst.type || 'solar',
      lat: parseFloat(inst.lat) || 0,
      lon: parseFloat(inst.lon) || 0,
      // Solar
      areaSqM: inst.areaSqM || 100,
      peakPowerW: inst.peakPowerW || 20000,
      tiltAngle: inst.tiltAngle || 30,
      azimuth: inst.azimuth || 180,
      // Wind
      hubHeightM: inst.hubHeightM || 80,
      ratedPowerKW: inst.ratedPowerKW || 2000,
      // Runtime state
      weatherData: null,
      riskScore: null,
      riskLevel: null,
      riskFactors: [],
      generation: [],
      hourlyGeneration: [],
      currentGenKW: 0,
      dailyGenKWh: 0,
      peakGenKW: 0,
      capacityFactor: 0,
      windProfile: null,
      alerts: [],
      status: 'pending',
      errorMsg: null,
    };
    entry.panelEfficiency = entry.peakPowerW / (entry.areaSqM * 1000);
    installations.value.push(entry);
    return entry;
  }

  function removeInstallation(id) {
    const idx = installations.value.findIndex(i => i.id === id);
    if (idx >= 0) installations.value.splice(idx, 1);
  }

  function updateInstallation(id, updates) {
    const inst = installations.value.find(i => i.id === id);
    if (!inst) return;
    Object.assign(inst, updates);
    if (inst.type === 'solar') {
      inst.panelEfficiency = inst.peakPowerW / (inst.areaSqM * 1000);
    }
  }

  function clearInstallations() {
    installations.value = [];
    hasRun.value = false;
  }

  // ── Date Range ─────────────────────────────────────────────

  function resolveDateRange() {
    const [startDate, endDate] = VcDateUtils.resolveDateRange(forecastPeriod.value, null);
    return {
      startDate: startDate ? VcDateUtils.formatDateIso(startDate) : null,
      endDate:   endDate   ? VcDateUtils.formatDateIso(endDate)   : null,
    };
  }

  // ── Data Fetching ──────────────────────────────────────────

  async function fetchAllInstallations() {
    if (!hasSession.value || installations.value.length === 0) return;

    isLoading.value = true;
    loadingProgress.value = 0;
    hasRun.value = false;
    errorMessage.value = null;

    const total = installations.value.length;
    let completed = 0;

    // Sequential to avoid overwhelming the API (could be batched with concurrency)
    for (const inst of installations.value) {
      try {
        inst.status = 'loading';
        await fetchInstallationWeather(inst);
        processInstallation(inst);
        inst.status = 'ok';
      } catch (err) {
        inst.status = 'error';
        inst.errorMsg = err.message;
      }
      completed++;
      loadingProgress.value = Math.round((completed / total) * 100);
    }

    isLoading.value = false;
    hasRun.value = true;
  }

  async function fetchInstallationWeather(inst) {
    const baseUrl = getServerRoot(serverRoot.value);
    const location = `${inst.lat},${inst.lon}`;
    const { startDate, endDate } = resolveDateRange();

    const url = `${baseUrl}/VisualCrossingWebServices/rest/services/timeline/${encodeURIComponent(location)}/${startDate}/${endDate}`;

    const postData = {
      key: apiKey.value,
      unitGroup: unitGroup.value,
      elements: ALL_ELEMENTS,
      include: 'days,hours,alerts',
      iconSet: 'icons2',
    };

    const response = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      redirect: 'error',
      referrerPolicy: 'strict-origin-when-cross-origin',
      body: JSON.stringify(postData),
    });

    if (!response.ok) {
      const txt = await response.text();
      throw new Error(txt || `HTTP ${response.status}`);
    }

    inst.weatherData = await response.json();
    inst.alerts = Array.isArray(inst.weatherData.alerts) ? inst.weatherData.alerts : [];
  }

  // ── Processing ─────────────────────────────────────────────

  function processInstallation(inst) {
    if (!inst.weatherData?.days?.length) return;

    const allHours = inst.weatherData.days.flatMap(d => d.hours || []);

    if (inst.type === 'solar') {
      processSolarInstallation(inst, allHours);
    } else {
      processWindInstallation(inst, allHours);
    }

    // Common risk factors
    const commonFactors = [];
    for (const h of allHours) {
      commonFactors.push(_lightningRisk(h));
      commonFactors.push(_floodingRisk(h));
      commonFactors.push(_temperatureExtremeRisk(h));
    }
    const avgCommon = commonFactors.length ? commonFactors.reduce((a, b) => a + b, 0) / commonFactors.length : 0;

    // Final risk = max of type-specific + common universal risks
    const alertRisk = _alertRiskScore(inst.alerts);
    inst.riskScore = Math.max(inst.riskScore || 0, Math.round(avgCommon), alertRisk);
    inst.riskLevel = riskLevel(inst.riskScore);

    // Add universal risk factors
    const maxLightning = allHours.reduce((mx, h) => Math.max(mx, _lightningRisk(h)), 0);
    const maxFlood = allHours.reduce((mx, h) => Math.max(mx, _floodingRisk(h)), 0);
    const maxTempExt = allHours.reduce((mx, h) => Math.max(mx, _temperatureExtremeRisk(h)), 0);
    inst.riskFactors.push(
      { name: 'Lightning', score: maxLightning, weight: 'universal', level: riskLevel(maxLightning) },
      { name: 'Flooding', score: maxFlood, weight: 'universal', level: riskLevel(maxFlood) },
      { name: 'Temperature Extremes', score: maxTempExt, weight: 'universal', level: riskLevel(maxTempExt) }
    );
  }

  function _alertRiskScore(alerts) {
    if (!alerts?.length) return 0;
    let max = 0;
    for (const a of alerts) {
      const sev = (a.severity || '').toLowerCase();
      if (sev.includes('extreme'))  max = Math.max(max, 100);
      else if (sev.includes('severe'))   max = Math.max(max, 75);
      else if (sev.includes('moderate')) max = Math.max(max, 50);
      else max = Math.max(max, 25);
    }
    return max;
  }

  function processSolarInstallation(inst, allHours) {
    // Type-specific risk scores per hour
    const hourlyRisks = allHours.map((h, i) => {
      const cloud = _solarCloudRisk(h);
      const dust  = _solarDustRisk(h);
      const heat  = _solarHeatRisk(h);
      const hail  = _solarHailRisk(h);
      const snow  = _solarSnowRisk(h);
      return _aggregateRisks([cloud, dust, heat, hail, snow]);
    });

    const maxRisk = Math.max(...hourlyRisks, 0);
    inst.riskScore = maxRisk;

    // Detailed risk factors (max observed)
    inst.riskFactors = [
      { name: 'Cloud Cover', score: allHours.reduce((mx, h) => Math.max(mx, _solarCloudRisk(h)), 0), weight: 'primary', level: '' },
      { name: 'Dust / Particles', score: allHours.reduce((mx, h) => Math.max(mx, _solarDustRisk(h)), 0), weight: 'secondary', level: '' },
      { name: 'Extreme Heat', score: allHours.reduce((mx, h) => Math.max(mx, _solarHeatRisk(h)), 0), weight: 'secondary', level: '' },
      { name: 'Hail', score: allHours.reduce((mx, h) => Math.max(mx, _solarHailRisk(h)), 0), weight: 'tertiary', level: '' },
      { name: 'Snow / Ice', score: allHours.reduce((mx, h) => Math.max(mx, _solarSnowRisk(h)), 0), weight: 'tertiary', level: '' },
    ];
    inst.riskFactors.forEach(f => f.level = riskLevel(f.score));

    // Hourly generation prediction
    const efficiency = inst.panelEfficiency || 0.20;
    inst.hourlyGeneration = allHours.map(h => {
      const ghi = h.ghiradiation ?? h.solarradiation ?? 0;
      const genKW = solarPower(ghi, inst.areaSqM, efficiency);
      return {
        datetime: h.datetime,
        datetimeEpoch: h.datetimeEpoch,
        genKW: Math.round(genKW * 100) / 100,
        ghi: ghi,
        dni: h.dniradiation ?? 0,
        dhi: h.difradiation ?? 0,
        gti: h.gtiradiation ?? 0,
        cloudcover: h.cloudcover ?? 0,
        temp: h.temp ?? 0,
      };
    });

    // Daily generation aggregation
    inst.generation = inst.weatherData.days.map(day => {
      const dayHours = (day.hours || []).map(h => {
        const ghi = h.ghiradiation ?? h.solarradiation ?? 0;
        return solarPower(ghi, inst.areaSqM, efficiency);
      });
      const totalKWh = dayHours.reduce((s, kw) => s + kw, 0); // each hour contributes 1h × kW = kWh
      const peakKW = Math.max(...dayHours, 0);
      return {
        datetime: day.datetime,
        totalKWh: Math.round(totalKWh * 100) / 100,
        peakKW: Math.round(peakKW * 100) / 100,
        icon: day.icon,
        conditions: day.conditions,
        tempmax: day.tempmax,
        tempmin: day.tempmin,
      };
    });

    // Summary stats
    const allGen = inst.hourlyGeneration.map(h => h.genKW);
    inst.currentGenKW = allGen.length ? allGen[0] : 0;
    inst.peakGenKW = Math.max(...allGen, 0);
    inst.dailyGenKWh = inst.generation.reduce((s, d) => s + d.totalKWh, 0) / (inst.generation.length || 1);
    const ratedKW = inst.peakPowerW / 1000;
    inst.capacityFactor = ratedKW > 0 ? Math.round((inst.dailyGenKWh / (ratedKW * 24)) * 100) : 0;
  }

  function processWindInstallation(inst, allHours) {
    // Wind profile at multiple altitudes
    inst.windProfile = allHours.map(h => {
      const baseWS = h.windspeed || 0;
      const profile = {};
      for (const alt of WIND_ALTITUDES) {
        // Use actual API fields if available, otherwise estimate
        if (alt === 80 && h.windspeed80 != null) {
          profile[alt] = h.windspeed80;
        } else if (alt === 100 && h.windspeed100 != null) {
          profile[alt] = h.windspeed100;
        } else {
          profile[alt] = Math.round(windSpeedAtHeight(baseWS, alt) * 10) / 10;
        }
      }
      return {
        datetime: h.datetime,
        datetimeEpoch: h.datetimeEpoch,
        baseSpeed: baseWS,
        ...profile,
      };
    });

    // Type-specific risk
    const hourlyRisks = allHours.map((h, i) => {
      const storm = _windStormRisk(h);
      const icing = _windIcingRisk(h);
      const turbu = _windTurbulenceRisk(h, allHours[i - 1]);
      return _aggregateRisks([storm, icing, turbu]);
    });
    const maxRisk = Math.max(...hourlyRisks, 0);
    inst.riskScore = maxRisk;

    inst.riskFactors = [
      { name: 'Storm-force Winds', score: allHours.reduce((mx, h) => Math.max(mx, _windStormRisk(h)), 0), weight: 'primary', level: '' },
      { name: 'Freezing Rain / Icing', score: allHours.reduce((mx, h) => Math.max(mx, _windIcingRisk(h)), 0), weight: 'secondary', level: '' },
      { name: 'Turbulence', score: allHours.reduce((mx, h, i) => Math.max(mx, _windTurbulenceRisk(h, allHours[i-1])), 0), weight: 'tertiary', level: '' },
    ];
    inst.riskFactors.forEach(f => f.level = riskLevel(f.score));

    // Hourly generation prediction using hub-height wind
    inst.hourlyGeneration = allHours.map(h => {
      const hubWS = inst.windProfile?.find(p => p.datetimeEpoch === h.datetimeEpoch)?.[inst.hubHeightM]
                  || windSpeedAtHeight(h.windspeed || 0, inst.hubHeightM);
      const genKW = windPower(hubWS, inst.ratedPowerKW);
      return {
        datetime: h.datetime,
        datetimeEpoch: h.datetimeEpoch,
        genKW: Math.round(genKW * 100) / 100,
        windSpeed: hubWS,
        windDir: h.winddir ?? 0,
        temp: h.temp ?? 0,
      };
    });

    // Daily generation
    inst.generation = inst.weatherData.days.map(day => {
      const dayHours = (day.hours || []).map(h => {
        const hubWS = windSpeedAtHeight(h.windspeed || 0, inst.hubHeightM);
        return windPower(hubWS, inst.ratedPowerKW);
      });
      const totalKWh = dayHours.reduce((s, kw) => s + kw, 0);
      const peakKW = Math.max(...dayHours, 0);
      return {
        datetime: day.datetime,
        totalKWh: Math.round(totalKWh * 100) / 100,
        peakKW: Math.round(peakKW * 100) / 100,
        icon: day.icon,
        conditions: day.conditions,
        windspeed: day.windspeed,
        windgust: day.windgust,
      };
    });

    // Summary
    const allGen = inst.hourlyGeneration.map(h => h.genKW);
    inst.currentGenKW = allGen.length ? allGen[0] : 0;
    inst.peakGenKW = Math.max(...allGen, 0);
    inst.dailyGenKWh = inst.generation.reduce((s, d) => s + d.totalKWh, 0) / (inst.generation.length || 1);
    inst.capacityFactor = inst.ratedPowerKW > 0 ? Math.round((inst.dailyGenKWh / (inst.ratedPowerKW * 24)) * 100) : 0;
  }

  // ── Session Management ─────────────────────────────────────

  function initSession() {
    const vc = window.vcCommunicator;
    if (!vc) {
      console.warn('useEnergyDashboardEngine: vcCommunicator not found');
      return;
    }

    sessionId.value = vc.sessionId;
    sessionState.value = vc.sessionState;
    isContainerInited.value = vc.isInited || !!vc.sessionId;

    vc.addHandler('createsession', (sid, state) => {
      sessionId.value = sid;
      sessionState.value = state;
      isContainerInited.value = true;
    });

    vc.addHandler('terminatesession', () => {
      sessionId.value = null;
      sessionState.value = null;
      installations.value = [];
      hasRun.value = false;
    });
  }

  // ── Unit helpers ───────────────────────────────────────────

  function setUnitGroup(ug) {
    unitGroup.value = ug;
    // Re-process if we have data
    if (hasRun.value) {
      for (const inst of installations.value) {
        if (inst.weatherData) processInstallation(inst);
      }
    }
  }

  function formatTemp(v) {
    if (v == null) return '–';
    return `${Math.round(v)}°${unitGroup.value === 'us' ? 'F' : 'C'}`;
  }

  function formatSpeed(v) {
    if (v == null) return '–';
    return `${Math.round(v)} ${unitGroup.value === 'us' ? 'mph' : 'km/h'}`;
  }

  function formatPower(kw) {
    if (kw == null) return '–';
    if (kw >= 1000) return `${(kw / 1000).toFixed(1)} MW`;
    return `${kw.toFixed(1)} kW`;
  }

  function formatEnergy(kwh) {
    if (kwh == null) return '–';
    if (kwh >= 1000) return `${(kwh / 1000).toFixed(1)} MWh`;
    return `${kwh.toFixed(1)} kWh`;
  }

  // ── Persistence ────────────────────────────────────────────

  const STORAGE_KEY = 'vc_energy_dashboard_installations';

  function saveInstallationsToStorage() {
    try {
      const data = installations.value.map(i => ({
        id: i.id, name: i.name, type: i.type,
        lat: i.lat, lon: i.lon,
        areaSqM: i.areaSqM, peakPowerW: i.peakPowerW,
        tiltAngle: i.tiltAngle, azimuth: i.azimuth,
        hubHeightM: i.hubHeightM, ratedPowerKW: i.ratedPowerKW,
      }));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) { /* ignore quota errors */ }
  }

  function loadInstallationsFromStorage() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const data = JSON.parse(raw);
      if (Array.isArray(data)) {
        for (const d of data) addInstallation(d);
      }
    } catch (e) { /* ignore */ }
  }

  // ── Public API ─────────────────────────────────────────────

  return {
    // Session
    sessionId, sessionState, hasSession, apiKey, isContainerInited,
    initSession,

    // Config
    unitGroup, forecastPeriod,
    setUnitGroup,

    // Installations
    installations, solarInstallations, windInstallations,
    attentionInstallations, overallSummary,
    addInstallation, removeInstallation, updateInstallation, clearInstallations,
    saveInstallationsToStorage, loadInstallationsFromStorage,

    // Data
    isLoading, loadingProgress, hasRun, errorMessage,
    fetchAllInstallations, resolveDateRange,

    // Utilities
    riskLevel, riskColor, riskIcon,
    formatTemp, formatSpeed, formatPower, formatEnergy,
    windSpeedAtHeight, windPower, solarPower,
    WIND_ALTITUDES,
  };
}
